// components/sales/sections/GA4TrafficByChannel.jsx

import { memo, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useGA4ActiveUsers } from "../../../hooks/sales/useGA4ActiveUsers";
import { getChartColors } from '../../../theme/colors';

const GA4TrafficByChannel = memo(({ dateRange }) => {
  // Always show last 6 months of the selected year
  const months = 6;
  
  // Check if selected year is current year
  const includeCurrentMonth = useMemo(() => {
    if (!dateRange?.start_date) return true;
    
    const selectedYear = parseInt(dateRange.start_date.split('-')[0], 10);
    const currentYear = new Date().getFullYear();
    
    return selectedYear === currentYear;
  }, [dateRange]);

  const { 
    chartData, 
    channelStats, 
    totals, 
    metadata, 
    topPerformers,
    loading, 
    error 
  } = useGA4ActiveUsers(months, includeCurrentMonth);

  const channelColors = getChartColors(['teal', 'brightblue', 'darkblue', 'green']);

  // Custom tooltip with detailed breakdown
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl text-sm min-w-[220px]">
          <p className="font-bold text-base mb-3 pb-2 border-b border-gray-700">
            {data.period}
          </p>
          
          <div className="space-y-2">
            {/* Total */}
            <div className="flex justify-between items-center pb-2 border-b border-gray-700">
              <span className="text-gray-300">Total Users:</span>
              <span className="font-bold text-white">{data.total.toLocaleString()}</span>
            </div>
            
            {/* Direct */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: channelColors[0] }}></div>
                <span className="text-gray-300">Direct:</span>
              </div>
              <span className="font-semibold">{data.Direct.toLocaleString()}</span>
            </div>
            
            {/* Organic */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: channelColors[1] }}></div>
                <span className="text-gray-300">Organic:</span>
              </div>
              <span className="font-semibold">{data.Organic.toLocaleString()}</span>
            </div>
            
            {/* Email */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: channelColors[2] }}></div>
                <span className="text-gray-300">Email:</span>
              </div>
              <span className="font-semibold">{data.Email.toLocaleString()}</span>
            </div>
            
            {/* Other */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: channelColors[3] }}></div>
                <span className="text-gray-300">Other:</span>
              </div>
              <span className="font-semibold">{data.Other.toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="h-6 bg-gray-200 rounded w-64 mb-4 animate-pulse"></div>
        <div className="h-[300px] bg-gray-100 rounded animate-pulse"></div>
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
          {[1, 2, 3].map((i) => (
            <div key={i} className="text-center">
              <div className="h-4 bg-gray-200 rounded w-24 mx-auto mb-2 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-16 mx-auto animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Website Traffic by Channel
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Active users by traffic source (Google Analytics 4)
          </p>
        </div>
        
        <div className="h-[300px] bg-red-50 rounded border border-red-200 flex items-center justify-center">
          <div className="text-red-600 text-center">
            <div className="text-4xl mb-3">⚠️</div>
            <p className="font-semibold">Error loading GA4 data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Website Traffic by Channel
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Active users by traffic source (Google Analytics 4)
          </p>
        </div>
        
        <div className="h-[300px] bg-gray-50 rounded border border-gray-200 flex items-center justify-center">
          <div className="text-gray-500 text-center">
            <div className="text-4xl mb-3">📊</div>
            <p className="font-semibold">No GA4 data available</p>
            <p className="text-sm mt-1">Data will appear once GA4 ETL runs</p>
          </div>
        </div>
      </div>
    );
  }

  const totalActiveUsers = metadata?.total_active_users || 0;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Website Traffic by Channel
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Monthly active users by acquisition channel
        </p>
        {metadata?.start_date && metadata?.end_date && (
          <p className="text-xs text-gray-400 mt-1">
            {new Date(metadata.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to{' '}
            {new Date(metadata.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        )}
        
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <span className="font-semibold">{totalActiveUsers.toLocaleString()}</span> total active users
          </div>
        </div>
      </div>
      
      <div style={{ height: "300px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              label={{ 
                value: 'Active Users', 
                angle: -90, 
                position: 'insideLeft',
                style: { fontSize: 12, fill: '#6b7280' }
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 147, 207, 0.1)' }} />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
              iconType="circle"
            />
            <Bar dataKey="Direct" stackId="a" fill={channelColors[0]} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Organic" stackId="a" fill={channelColors[1]} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Email" stackId="a" fill={channelColors[2]} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Other" stackId="a" fill={channelColors[3]} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
        {/* Top Channel */}
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">Top Channel</div>
          {topPerformers?.topChannel ? (
            <>
              <div className="text-xl font-bold text-gray-900">
                {topPerformers.topChannel.channel}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {topPerformers.topChannel.percentage_of_total.toFixed(1)}% of traffic
              </div>
            </>
          ) : (
            <div className="text-xl font-bold text-gray-400">--</div>
          )}
        </div>

        {/* Most Growth */}
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">Most Growth</div>
          {topPerformers?.mostGrowth ? (
            <>
              <div className="text-xl font-bold text-gray-900">
                {topPerformers.mostGrowth.channel}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                +{topPerformers.mostGrowth.growthPercent.toFixed(1)}% growth
              </div>
            </>
          ) : (
            <div className="text-xl font-bold text-gray-400">--</div>
          )}
        </div>

        {/* Total Traffic */}
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">Total Traffic</div>
          <div className="text-xl font-bold text-gray-900">
            {totalActiveUsers.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            active users
          </div>
        </div>
      </div>
    </div>
  );
});

export default GA4TrafficByChannel;